/**
 * Breadcrumbs Component
 * 
 * Modern breadcrumb navigation with:
 * - Automatic path parsing
 * - Clickable navigation
 * - Recent pages dropdown
 * - Home shortcut
 * 
 * @component
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Breadcrumbs as MuiBreadcrumbs,
    Link,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Chip
} from '@mui/material';
import {
    Home as HomeIcon,
    NavigateNext as NavigateNextIcon,
    History as HistoryIcon,
    Delete as DeleteIcon,
    Star as StarIcon
} from '@mui/icons-material';
import useRecentPages from '../../hooks/useRecentPages';

// Route segment to label mapping - keys for i18n
const SEGMENT_LABELS = {
    admin: 'Admin',
    client: 'Client',
    manager: 'Manager',
    operator: 'Operator',
    dashboard: 'Dashboard',
    orders: 'Orders',
    products: 'Products',
    inventory: 'Inventory',
    users: 'Users',
    warehouses: 'Warehouses',
    shipping: 'Shipping',
    'hr-management': 'HR',
    'financial-management': 'Finance',
    analytics: 'Analytics',
    settings: 'Settings',
    reports: 'Reports',
    'marketplace-connections': 'Marketplaces',
    'ai-assistant': 'AI Assistant',
    'ai-actions-dashboard': 'AI Actions',
    configuration: 'Configuration'
};

const getSegmentLabel = (segment) => {
    return SEGMENT_LABELS[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
};

const Breadcrumbs = ({ showRecentPages = true }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { recentPages, _removePage, clearHistory } = useRecentPages();
    const [anchorEl, setAnchorEl] = useState(null);

    // Parse current path into segments
    const pathSegments = location.pathname.split('/').filter(Boolean);

    // Build cumulative paths for each segment
    const breadcrumbItems = pathSegments.map((segment, index) => {
        const path = '/' + pathSegments.slice(0, index + 1).join('/');
        const label = getSegmentLabel(segment);
        const isLast = index === pathSegments.length - 1;

        return { path, label, isLast };
    });

    const handleClick = (path) => {
        navigate(path);
    };

    const handleRecentMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleRecentMenuClose = () => {
        setAnchorEl(null);
    };

    const handleRecentPageClick = (path) => {
        handleRecentMenuClose();
        navigate(path);
    };

    const formatTimeAgo = (timestamp) => {
        const diff = Date.now() - timestamp;
        const minutes = Math.floor(diff / 60000);
        if (minutes < 1) return t('common.time.justNow', 'just now');
        if (minutes < 60) return t('common.time.minutesAgo', '{{count}} min ago', { count: minutes });
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return t('common.time.hoursAgo', '{{count}}h ago', { count: hours });
        return t('common.time.daysAgo', '{{count}}d ago', { count: Math.floor(hours / 24) });
    };

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 1,
            px: 2,
            backgroundColor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1
        }}>
            {/* Home button */}
            <Tooltip title={t('common.home', 'Home')}>
                <IconButton
                    size="small"
                    onClick={() => navigate('/admin/dashboard')}
                    sx={{ color: 'primary.main' }}
                >
                    <HomeIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            {/* Breadcrumbs */}
            <MuiBreadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                sx={{ flex: 1 }}
            >
                {breadcrumbItems.map(({ path, label, isLast }) => (
                    isLast ? (
                        <Typography key={path} color="text.primary" fontWeight="medium">
                            {label}
                        </Typography>
                    ) : (
                        <Link
                            key={path}
                            component="button"
                            underline="hover"
                            color="inherit"
                            onClick={() => handleClick(path)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {label}
                        </Link>
                    )
                ))}
            </MuiBreadcrumbs>

            {/* Recent pages dropdown */}
            {showRecentPages && recentPages.length > 0 && (
                <>
                    <Tooltip title={t('common.recentPages', 'Recent Pages')}>
                        <IconButton size="small" onClick={handleRecentMenuOpen}>
                            <HistoryIcon fontSize="small" />
                            <Chip
                                label={recentPages.length}
                                size="small"
                                sx={{
                                    ml: 0.5,
                                    height: 18,
                                    fontSize: '0.65rem',
                                    '& .MuiChip-label': { px: 0.5 }
                                }}
                            />
                        </IconButton>
                    </Tooltip>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleRecentMenuClose}
                        PaperProps={{
                            sx: { minWidth: 250, maxHeight: 400 }
                        }}
                    >
                        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2" color="text.secondary">
                                {t('common.recentPages', 'Recent Pages')}
                            </Typography>
                            <IconButton size="small" onClick={clearHistory}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        {recentPages.slice(1).map((page) => (
                            <MenuItem
                                key={page.path}
                                onClick={() => handleRecentPageClick(page.path)}
                                sx={{ py: 1 }}
                            >
                                <ListItemText
                                    primary={page.title}
                                    secondary={formatTimeAgo(page.timestamp)}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                    secondaryTypographyProps={{ variant: 'caption' }}
                                />
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            )}
        </Box>
    );
};

export default React.memo(Breadcrumbs);
